// Copyright (c) Microgreen Manager LLC. All rights reserved. Proprietary and confidential. Unauthorized copying is strictly prohibited.
import { PLANTING_STATUS } from '@seedplanner/shared-utils';
// biome-ignore lint/style/useImportType: <explanation>
import * as React from 'react';
import { useUserContext } from '../../context/UserContext';
import { formatStateOrStatus, getID } from '../../utils';
import { formatNumber } from '../../utils/formatNumber';
import { VisibleAtContainer } from '../common/containers/VisibleAtContainer';
import { Table } from '../common/table';
import type { ColumnType } from '../common/table/Table';
import { Time } from '../common/time';
import { CropIdLink } from '../crop';
import HarvestLink from '../harvest/HarvestLink';
import { PlantingGroupIdLink } from '../plantingGroup';

interface PlantingData {
  readonly id: string;
  readonly harvestTime: number;
  readonly plantingStatus: string;
  readonly plantedAt?: number | null;
  readonly totalTrays: number;
  readonly crop?: {
    readonly id: string;
    readonly crop_name: string;
  } | null;
  readonly currentStage?: string;
}
interface Props {
  readonly plantings: PlantingData[];
}

export default function PlantingGroupsHomeTable({ plantings }: Props) {
  const { language } = useUserContext();
  const idValues: React.ReactElement[] = [];
  const statusValues: string[] = [];
  const timeValues: React.ReactElement[] = [];
  const traysValues: string[] = [];
  const currentStageValues: (React.ReactElement | string)[] = [];
  const plantedAtValues: (React.ReactElement | string)[] = [];
  const cropValues: (React.ReactElement | string)[] = [];
  for (const planting of plantings) {
    const plantingId = getID(planting.id);
    idValues.push(
      <VisibleAtContainer
        up={<PlantingGroupIdLink id={plantingId} />}
        down={<PlantingGroupIdLink id={plantingId} title={plantingId} />}
      />,
    );
    cropValues.push(planting.crop ? <CropIdLink name={planting.crop.crop_name} id={getID(planting.crop.id)} /> : '');
    statusValues.push(formatStateOrStatus(planting.plantingStatus));
    timeValues.push(
      <HarvestLink title={<Time time={planting.harvestTime} options={{ removeDayOfWeek: true }} />} id={plantingId} />,
    );
    traysValues.push(formatNumber(planting.totalTrays, language));
    currentStageValues.push(
      planting.currentStage === PLANTING_STATUS.PLANNED || planting.currentStage === PLANTING_STATUS.COMPLETED
        ? ''
        : (planting.currentStage ?? ''),
    );
    plantedAtValues.push(
      planting.plantedAt ? <Time time={planting.plantedAt} options={{ removeDayOfWeek: true }} /> : '',
    );
  }
  const columns: ColumnType[] = [
    {
      name: 'Planting',
      values: idValues,
    },
    {
      name: 'Crop',
      values: cropValues,
    },
    {
      name: 'Trays',
      values: traysValues,
      numeric: true,
    },
    {
      name: 'Status',
      values: statusValues,
      visibleAt: 'lg',
    },
    {
      name: 'Current Stage',
      smallName: 'Stage',
      values: currentStageValues,
    },
    {
      name: 'Planted On',
      values: plantedAtValues,
      visibleAt: 'md',
    },
    {
      name: 'Harvest Date',
      values: timeValues,
      visibleAt: 'sm',
    },
  ];

  return <Table columns={columns} />;
}
